// Public surface of the local print-app integration.
//
// All print jobs flow through one path: open a WebSocket to Chit Chats Print,
// send a print command (`print_shipments` for token-based jobs, `print` for
// URL-list jobs), and read progress events. Chit Chats Print fetches every
// label directly from Rails in parallel, merges them into a single artifact,
// and submits one OS print job. Browser is not on the data path.
//
import { createPrintApiClient } from "./chit_chats_print_agent_api_client";
import { createPrintErrorMapper } from "./chit_chats_print_agent_error_mapper";
import { createPrintWSFlow } from "./chit_chats_print_agent_ws_flow";
import { runConnectivityProbe, getBaseUrl } from "./chit_chats_print_agent_connectivity";

function buildServiceError(code, message) {
  var error = new Error(message || "");
  error.code = code;
  return error;
}

const ChitChatsPrintService = {
  errorMessages: {},

  setErrorMessages(messages) {
    this.errorMessages = messages || {};
    this.apiClient = createPrintApiClient(this.message.bind(this));
    this.errorMapper = createPrintErrorMapper(this.message.bind(this));
    this.wsFlow = createPrintWSFlow(this.apiClient, this.message.bind(this));
  },

  printShipments(payload, printerId, options) {
    if (!payload || !payload.shipment_tokens || payload.shipment_tokens.length === 0) {
      return Promise.reject(buildServiceError("EMPTY_JOB", this.message("no_labels_available")));
    }
    return this.wsFlow.runShipments(payload, printerId, options || {});
  },

  printURLs(payload, printerId, options) {
    if (!payload || !payload.urls || payload.urls.length === 0) {
      return Promise.reject(buildServiceError("EMPTY_JOB", this.message("no_labels_available")));
    }
    return this.wsFlow.runURLs(payload, printerId, options || {});
  },

  listPrinters() {
    return this.apiClient.listPrinters();
  },

  message(key, fallback) {
    if (this.errorMessages && this.errorMessages[key]) {
      return this.errorMessages[key];
    }
    return fallback || "";
  },

  toUserMessage(error) {
    return this.errorMapper.toUserMessage(error);
  },

  // RunConnectivityProbe — surfaced on the service so the print modal's
  // failure UX can check connectivity before re-attempting a print
  // without dragging in the connectivity module directly. The print
  // modal lives in a Rails ERB partial that already talks to this
  // global, so this is the cheapest integration point.
  //
  // Important: this is a transient health check. It does not mutate the
  // setup-page status UI; print-time retries are not setup events.
  runConnectivityProbe(baseUrl) {
    return runConnectivityProbe(baseUrl);
  },

  getBaseUrl() {
    return getBaseUrl();
  }
};

ChitChatsPrintService.setErrorMessages({});

export { ChitChatsPrintService };
